import type { Message } from '../types';

/** The reactions offered first, in the order they appear. Everything else is behind "+". */
export const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏', '✨'] as const;

export interface ReactionPill {
  emoji: string;
  count: number;
  /** The signed-in user is one of the people who chose it. */
  mine: boolean;
}

type Reacted = Pick<Message, 'reaction' | 'reactions'>;

function ownReactions(msg: Reacted): Record<string, string> | null {
  const map = msg.reactions;
  if (map && typeof map === 'object' && !Array.isArray(map) && Object.keys(map).length > 0) return map;
  return null;
}

/** The signed-in user's own reaction on a message, if any. */
export function myReaction(msg: Reacted, userId: string): string | undefined {
  return ownReactions(msg)?.[userId];
}

/**
 * What to show under a message: one pill per emoji with how many people chose it,
 * most chosen first. A reaction saved by an app that only knows the single
 * `reaction` field has no owner and shows as one pill nobody can call their own.
 */
export function reactionPills(msg: Reacted, userId: string): ReactionPill[] {
  const map = ownReactions(msg);
  if (!map) return msg.reaction ? [{ emoji: msg.reaction, count: 1, mine: false }] : [];
  const byEmoji = new Map<string, ReactionPill>();
  for (const [who, emoji] of Object.entries(map)) {
    if (typeof emoji !== 'string' || !emoji) continue;
    const pill = byEmoji.get(emoji) ?? { emoji, count: 0, mine: false };
    pill.count += 1;
    if (who === userId) pill.mine = true;
    byEmoji.set(emoji, pill);
  }
  return [...byEmoji.values()].sort((a, b) => b.count - a.count);
}

/**
 * The message as it will be once the user's reaction is `emoji` (undefined removes
 * it). Mirrors what set_my_message_reaction stores, so the screen shows the result
 * before the server confirms it.
 */
export function withMyReaction(msg: Message, userId: string, emoji: string | undefined): Message {
  const map = { ...(ownReactions(msg) ?? {}) };
  if (emoji) map[userId] = emoji;
  else delete map[userId];
  const { reaction: _old, reactions: _oldMap, ...rest } = msg;
  void _old;
  void _oldMap;
  const first = Object.keys(map).sort()[0];
  return first ? { ...rest, reactions: map, reaction: map[first] } : rest;
}

/** Tapping the reaction you already have takes it away. */
export function nextReaction(current: string | undefined, picked: string): string | undefined {
  return current === picked ? undefined : picked;
}
